import { Button, CountDownTimer, FontIcon, Text } from '@intigral/shared';
import moment from 'moment';
import styles from '../styles/tvguide.module.sass';
import { IProgramDetails } from '../../types/iProgram';
import { useEpgLocalization, usePpvEvent } from '../../hooks';

export const ProgramDetails: React.FC<IProgramDetails> = ({
    selectedProgram,
    lang,
    AddReminderButton,
    AddWatchListButton,
    isFutureProgram,
    isFavorite,
    onFavoriteClick,
    onPPVPurchaseClick,
}) => {
    // Hooks must run unconditionally (before any early return).
    const localization = useEpgLocalization();
    const {
        displayTitle,
        displayDescription,
        effectiveIsPPVPurchased,
        effectivePPVEventId,
        effectivePPVPrice,
        effectivePPVCurrency,
        ppv_description_text,
    } = usePpvEvent(selectedProgram?.channel, selectedProgram);

    if (!selectedProgram) return null;

    const { since, till, channel, id, paId, programType, index, isPPV, ppvEvent } = selectedProgram;

    return (
        <section className={styles['program-details-container']}>
            {/* Title & Channel Number */}
            <Text as="h2" variant="h2" margin="0" fontWeight="medium" alignment="start" size={{ md: '24' }}>
                <span className={styles.number}>{channel?.channelNumber ?? ''}</span>
                {displayTitle}
            </Text>

            {/* Program Time */}
            <Text as="p" alignment="start" size={{ md: '14' }} className={styles['program-time']}>
                {lang === 'ar'
                    ? `${(() => {
                          try {
                              return moment(since).locale('ar').format('MMM DD, HH:mm');
                          } catch (error) {
                              return moment(since).locale('en').format('MMM DD, HH:mm');
                          }
                      })()} - ${(() => {
                          try {
                              return moment(till).locale('ar').format('MMM DD, HH:mm');
                          } catch (error) {
                              return moment(till).locale('en').format('MMM DD, HH:mm');
                          }
                      })()}`
                    : `${moment(since).format('MMM DD, HH:mm')} - ${moment(till).format('MMM DD, HH:mm')}`}
            </Text>

            {/* Content availability duration */}
            {ppvEvent && ppvEvent.eventBroadcastTs && ppvEvent.eventBroadcastTs < Date.now() && (
                <div>
                    <br />
                    <Text as="p" variant="text5" alignment="start" color={effectiveIsPPVPurchased ? 'green' : 'red'}>
                        {ppv_description_text}
                    </Text>
                </div>
            )}

            {/* PPV Event Countdown Timer */}
            {ppvEvent && ppvEvent.eventBroadcastTs && ppvEvent.eventBroadcastTs > Date.now() && (
                <div>
                    <br />
                    <CountDownTimer
                        timeStamp={ppvEvent.eventBroadcastTs}
                        labelDay={'days'}
                        labelHour={'hours'}
                        labelMinute={'minutes'}
                        labelSecond={'seconds'}
                    />
                    <br />
                </div>
            )}

            {/* Action Buttons */}
            <div className={styles.ctas}>
                {/* Show PPV Purchase Button if it's a PPV program and not purchased */}
                {isPPV && !effectiveIsPPVPurchased && (
                    <Button
                        compact
                        onClick={() => {
                            const eventId = ppvEvent?.eventId || effectivePPVEventId;
                            if (onPPVPurchaseClick && eventId) {
                                onPPVPurchaseClick(eventId);
                            }
                        }}
                    >
                        {localization?.ppv_buy_now({ amount: effectivePPVPrice, currency: effectivePPVCurrency })}
                    </Button>
                )}

                {(!isPPV || (isPPV && effectiveIsPPVPurchased)) &&
                    (isFutureProgram ? (
                        <AddReminderButton
                            item={selectedProgram}
                            index={index}
                            isComingSoon
                            category="LINEAR"
                            selectedChannel={channel}
                            secondary
                        />
                    ) : (
                        <AddWatchListButton
                            item={{ Id: id, paId, programType, category: 'linear' }}
                            type="ProgramItem"
                            watchlistButton
                        />
                    ))}
                {(!isPPV || (isPPV && effectiveIsPPVPurchased)) && (
                    <Button compact secondary onClick={() => onFavoriteClick?.(channel?.channelID)}>
                        {isFavorite ? localization?.remove_favorite_button_label : localization?.favorite_button_label}
                        <FontIcon
                            name={isFavorite ? 'heart-filled' : 'heart'}
                            size="smaller"
                            md="smaller"
                            color={isFavorite ? 'orange' : ''}
                        />
                    </Button>
                )}
            </div>

            {/* Description */}
            <div className={styles.details}>
                <Text as="p" alignment="start" margin="2x">
                    {displayDescription}
                </Text>
            </div>
        </section>
    );
};
